<script lang="ts">
  import { page } from '$app/state'
  import { confetti, sparkles } from '$lib/attachments/party-effect'
  import { play } from '$lib/sound'
  import type { Outfit, UserAccessory } from '$lib/types'
  import { getRarity } from '$lib/utils/accessories'
  import { onMount } from 'svelte'
  import { expoOut, quintIn } from 'svelte/easing'
  import { fly, type TransitionConfig } from 'svelte/transition'
  import Flash from './effects/Flash.svelte'
  import GodRays from './effects/GodRays.svelte'
  import LightMotes from './effects/LightMotes.svelte'
  import { prefersReducedMotion } from './effects/motion'
  import { shake } from './effects/shake'
  import ShineSweep from './effects/ShineSweep.svelte'
  import ShockwaveRing from './effects/ShockwaveRing.svelte'
  import SummonPillar from './effects/SummonPillar.svelte'

  /** Movements performed while showing off a new head or body accessory. */
  const SHOWCASE_MOVEMENTS = {
    head: 'pose-head-reveal',
    body: 'pose-body-reveal',
  }

  /** Minimum buildup, aligned with the drumroll in 'accessory-start'. */
  const MIN_CHARGE_DURATION = 2350
  /** Reveal even if the robot is still loading after this long. */
  const MAX_CHARGE_DURATION = 10_000

  interface Props {
    accessory: UserAccessory
    onContinue: () => void
    onEquip: () => void
  }

  let { accessory, onContinue, onEquip }: Props = $props()

  let rarity = $derived(getRarity(accessory))
  let profile = $derived(page.data.session?.profile)

  let outfit: Outfit = $derived({
    head:
      accessory.type === 'head'
        ? accessory.id
        : (profile?.accessory_head ?? null),
    body:
      accessory.type === 'body'
        ? accessory.id
        : (profile?.accessory_body ?? null),
    color: profile?.color ?? null,
  })
  let movement = $derived(
    accessory.type === 'pose'
      ? `pose-${accessory.id}`
      : SHOWCASE_MOVEMENTS[accessory.type],
  )

  let alreadyEquipped = $derived.by(() => {
    if (!profile) return false
    switch (accessory.type) {
      case 'head':
        return profile.accessory_head === accessory.id
      case 'body':
        return profile.accessory_body === accessory.id
      case 'pose':
        return profile.accessory_pose === accessory.id
    }
  })

  let revealed = $state(false)
  let pillarGone = $state(false)
  let actionsReady = $state(false)

  // Not $state: nothing renders from these, they only gate the reveal.
  let robotReady = false
  let minChargeElapsed = false

  const reveal = () => {
    if (revealed) return
    revealed = true
    play('accessory-reveal')
    if (rarity === 'epic' || rarity === 'legendary') {
      setTimeout(() => play('cheering'), 400)
    }
    setTimeout(() => (pillarGone = true), 700)
    setTimeout(() => (actionsReady = true), 1100)
  }

  const maybeReveal = () => {
    if (robotReady && minChargeElapsed) reveal()
  }

  onMount(() => {
    play('accessory-start')
    const minChargeTimer = setTimeout(
      () => {
        minChargeElapsed = true
        maybeReveal()
      },
      prefersReducedMotion() ? 0 : MIN_CHARGE_DURATION,
    )
    const capTimer = setTimeout(reveal, MAX_CHARGE_DURATION)
    return () => {
      clearTimeout(minChargeTimer)
      clearTimeout(capTimer)
    }
  })

  const stampIn = (
    _: Element,
    { delay = 0 }: { delay?: number },
  ): TransitionConfig => ({
    delay,
    duration: 450,
    css: (t: number) => `
      transform: scale(${3 - 2 * quintIn(t)});
      opacity: ${Math.min(1, t * 4)};
    `,
  })
</script>

<div
  class="scene"
  class:revealed
  style:--slow={rarity === 'legendary' ? 1.25 : 1}
  {@attach shake(revealed && rarity === 'legendary')}
>
  {#if revealed}
    <GodRays intense={rarity === 'epic' || rarity === 'legendary'} />
    <LightMotes mode="float" />
    <Flash />
    <ShockwaveRing />
  {:else}
    <LightMotes mode="attract" count={24} />
  {/if}

  {#if !pillarGone}
    <SummonPillar phase={revealed ? 'burst' : 'charge'} />
  {/if}

  <div class="center">
    <div class="robot-holder">
      {#await import('$lib/components/robot/Robot.svelte') then robotModule}
        <robotModule.default
          {outfit}
          {movement}
          onLoaded={() => {
            robotReady = true
            maybeReveal()
          }}
          onLoadFailed={() => {
            robotReady = true
            maybeReveal()
          }}
        />
      {/await}
      {#if revealed}
        <div class="burst-anchor" {@attach confetti}></div>
        <div class="burst-anchor" {@attach sparkles({ color: 'random' })}></div>
      {/if}
    </div>
  </div>

  <!-- Anchored below the centerpiece so appearing content never reflows it. -->
  <div class="lower">
    {#if revealed}
      <div class="caption">
        <div class="stamp" in:stampIn={{ delay: 350 }}>
          {rarity}
          <ShineSweep delay={1200} />
        </div>
        <h2
          class="name"
          in:fly={{ y: 24, duration: 500, delay: 600, easing: expoOut }}
        >
          {accessory.name}
        </h2>
        <p
          class="kind"
          in:fly={{ y: 16, duration: 400, delay: 750, easing: expoOut }}
        >
          New {accessory.type === 'pose'
            ? 'pose'
            : `${accessory.type} accessory`}
        </p>
      </div>
    {/if}

    {#if actionsReady}
      <div class="actions" in:fly={{ y: 16, duration: 350, easing: expoOut }}>
        {#if !alreadyEquipped}
          <button class="@button +accent" onclick={onEquip}>Equip</button>
        {/if}
        <button class="@button" onclick={onContinue}>Continue</button>
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">
  .scene {
    position: absolute;
    inset: 0;
  }
  .center {
    display: grid;
    position: absolute;
    place-items: center;
    inset: 0;
    pointer-events: none;
  }
  .lower {
    display: flex;
    position: absolute;
    /* Starts below the framed robot card, whatever appears here. */
    top: calc(50% + min(30vmin, 12rem) + 2rem);
    right: 0;
    left: 0;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }
  .robot-holder {
    position: relative;
    /* Framed like AccessoryPreview: the robot animation may fill the canvas,
       so the frame makes the cut-off read as a viewport, not a glitch. */
    border: 3px solid var(--reveal-ring);
    border-radius: var(--radius-xl);
    box-shadow: 0 0 2.5rem
      color-mix(in oklab, var(--reveal-ring) 40%, transparent);
    background: oklch(20% 0.02 270);
    overflow: hidden;
    width: min(60vmin, 24rem);
    opacity: 0;
    .revealed & {
      animation: robot-land calc(var(--slow) * 900ms)
        cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    }
  }
  .burst-anchor {
    position: absolute;
    top: 30%;
    left: 50%;
    width: 1px;
    height: 1px;
  }
  @keyframes robot-land {
    0% {
      transform: scale(0.3) translateY(4rem);
      opacity: 0;
    }
    30% {
      opacity: 1;
    }
    55% {
      transform: scale(1.08, 1.08) translateY(-0.5rem);
    }
    75% {
      transform: scale(0.97, 0.97) translateY(0);
    }
    100% {
      transform: scale(1) translateY(0);
      opacity: 1;
    }
  }
  .caption {
    display: flex;
    position: relative;
    flex-direction: column;
    align-items: center;
    gap: 0.625rem;
    text-align: center;
  }
  .stamp {
    position: relative;
    border: 2px solid var(--reveal-ring);
    border-radius: var(--radius-full);
    padding: 0.25rem 1rem;
    color: var(--reveal-hue);
    font-weight: var(--weight-bold);
    font-size: var(--scale-1);
    letter-spacing: 0.2em;
    text-transform: uppercase;
  }
  .name {
    color: oklch(97% 0.005 270);
    font-weight: var(--weight-bold);
    font-size: var(--scale-4);
    line-height: 1.1;
  }
  .kind {
    color: oklch(78% 0.01 270);
    font-size: var(--scale-0);
  }
  .actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }
  @media (prefers-reduced-motion: reduce) {
    .robot-holder {
      .revealed & {
        animation: robot-fade 400ms ease-out forwards;
      }
    }
    @keyframes robot-fade {
      to {
        opacity: 1;
      }
    }
  }
</style>
